import { useState, useEffect, useRef, useCallback } from 'react';
import { Video, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import FileDropzone, { VIDEO_UPLOAD, resolveMediaUrl } from './FileDropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MediaEditor = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/profile`);
        setVideoUrl(res.data.video_header_url || '');
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    })();
  }, []);

  const autoSave = useCallback((url) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await axios.put(`${API}/profile`, { video_header_url: url }); }
      catch { toast.error('Failed to save'); }
    }, 800);
  }, []);

  const handleChange = (url) => {
    setVideoUrl(url);
    autoSave(url);
  };

  const handleUploadedVideo = async (upload) => {
    const res = await axios.put(`${API}/profile`, { video_header_url: upload.url });
    setVideoUrl(res.data.video_header_url || upload.url);
  };

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  if (loading) {
    return <div className="max-w-2xl flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  const ytId = getYouTubeId(videoUrl);

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="font-outfit font-bold text-2xl text-white">Profile Video</h1>
        <p className="text-[#A1A1AA] mt-1">Add a featured video to your profile</p>
      </div>

      <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
            <Video className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h2 className="font-outfit font-semibold text-lg text-white">Video Header</h2>
            <p className="text-[#71717A] text-xs">Shows as a playable video section on your profile</p>
          </div>
        </div>
        <FileDropzone
          {...VIDEO_UPLOAD}
          testId="profile-video-dropzone"
          label="Choose a profile video or drag and drop"
          description="MP4, MOV, WebM, AVI or MKV - max 50MB"
          onUploaded={handleUploadedVideo}
          successMessage="Profile video uploaded"
          className="mb-4"
        />
        <div>
          <Label className="text-[#EDEDED]">YouTube or Video URL</Label>
          <Input data-testid="video-url-input" value={videoUrl}
            onChange={(e) => handleChange(e.target.value)}
            className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white"
            placeholder="https://youtube.com/watch?v=..." />
        </div>
        {ytId && (
          <div className="mt-4 rounded-xl overflow-hidden">
            <iframe className="w-full aspect-video"
              src={`https://www.youtube.com/embed/${ytId}`}
              allow="accelerometer; encrypted-media" allowFullScreen title="Preview" />
          </div>
        )}
        {videoUrl && !ytId && (
          <div className="mt-4 rounded-xl overflow-hidden">
            <video className="w-full aspect-video object-cover" src={resolveMediaUrl(videoUrl)} controls />
          </div>
        )}
        {videoUrl && (
          <Button variant="outline" size="sm" onClick={() => handleChange('')}
            className="mt-3 text-red-400 border-red-400/20 hover:bg-red-500/10 gap-2">
            <Trash2 className="w-3.5 h-3.5" /> Remove Video
          </Button>
        )}
      </div>
    </div>
  );
};

export default MediaEditor;
